import log, { METRIC_NAME } from "../logging";
import { getPortalConfig } from "../configurations";
import {
    getDeviceBrowser,
    getDeviceModel,
    getDeviceOSName,
    getDeviceTouchInfo,
    getDeviceType,
    isInternalTestTraffic,
} from "../utils/userAgentUtils";
import {
    fetchStreamingURLCache,
    refreshStreamingURLCache,
} from "../utils/StreamingURLCache";
import { useSessionLaunchMetricStore } from "./useSessionLaunchMetricStore";
import { useCreateRemoteBrowser } from "./api/useCreateRemoteBrowser";
import { CookieResult } from "../types/cookies";
import { useCallback } from "react";
import { CreateRemoteBrowserResponse } from "@amzn/ermine-ssr-sdk/clients/ermineserversiderenderingservice";

const publishDeviceMetrics = () => {
    log.publishMetricWithDimension(
        METRIC_NAME.DEVICE_BROWSER,
        getDeviceBrowser()
    );
    log.publishMetricWithDimension(METRIC_NAME.DEVICE_TYPE, getDeviceType());
    log.publishMetricWithDimension(METRIC_NAME.DEVICE_MODEL, getDeviceModel());
    log.publishMetricWithDimension(METRIC_NAME.DEVICE_OS, getDeviceOSName());
    log.publishMetricWithDimension(
        METRIC_NAME.DEVICE_TOUCHSCREEN,
        getDeviceTouchInfo()
    );
};

export const useGetStreamingUrl = () => {
    const setCachedSessionEndpointLoadedTimestampIfEmpty = useSessionLaunchMetricStore(
        (store) => store.setCachedSessionEndpointLoadedTimestampIfEmpty
    );
    const setSessionEndpointCreatedTimestampIfEmpty = useSessionLaunchMetricStore(
        (store) => store.setSessionEndpointCreatedTimestampIfEmpty
    );
    const createRemoteBrowser = useCreateRemoteBrowser();

    return useCallback(
        async (cookieResult: CookieResult) => {
            const cachedStreamingURL = fetchStreamingURLCache();

            if (cachedStreamingURL !== undefined) {
                const { streamingURL } = cachedStreamingURL;
                log.publishCounterMetric(METRIC_NAME.STREAMING_URL_CACHE_HIT);
                setCachedSessionEndpointLoadedTimestampIfEmpty();

                return streamingURL;
            }

            const portalConfig = getPortalConfig();
            const data = await createRemoteBrowser({
                portalArn: portalConfig.portalArn,
                cookies: cookieResult.cookies,
            });

            try {
                log.publishHttpStatusMetric(
                    METRIC_NAME.CREATE_REMOTE_BROWSER,
                    200
                );
                setSessionEndpointCreatedTimestampIfEmpty();

                if (!isInternalTestTraffic()) {
                    publishDeviceMetrics();
                }

                // Refill cache and log warning in case it failed
                const isCacheWriteSuccess = refreshStreamingURLCache(
                    data.streamingUrl
                );
                if (isCacheWriteSuccess === false) {
                    await log.warn(
                        "StreamingURL session cache write back failed."
                    );
                }

                return data.streamingUrl;
            } catch (err) {
                await log.error("Failed to consume fresh streaming URL", err);
                throw err;
            }
        },
        [
            setCachedSessionEndpointLoadedTimestampIfEmpty,
            setSessionEndpointCreatedTimestampIfEmpty,
            createRemoteBrowser,
        ]
    );
};
